<template>
  <div>
    <page-title
      :isSubmitting="isSubmitting"
      :savingDraft="savingDraft"
      :cancel-route="{
        name: 'member-details',
        params: { memberCk: memberDetails.member.memberCk }
      }"
      :submitEnabled="allFormsReady"
      @submit="submit"
      @saveDraft="saveDraft"
    />

    <cloud-cannon-content
      class="_p0 _ptxs bg-light-gray"
      :content-loading="cloudCannonContentLoading"
      :content="cloudCannonContent.header"
    />

    <accordion>
      <personal-information
        :memberLocked="memberLocked"
        :memberDetailsLoading="memberDetailsLoading"
        :memberIdSource="computedMemberIdSource"
        :isDependent="isDependent"
        v-model="computedPersonalInformation"
        @ready="isReady => (ready.personal = isReady)"
      />

      <member-coverage
        v-model="computedPersonalInformation"
        :memberDetailsLoading="memberDetailsLoading"
        @ready="isReady => (ready.coverage = isReady)"
      >
        <template #collapser><accordion-collapser /></template>
      </member-coverage>

      <related-information />

      <contact-information
        :canEditHomeAddress="canEditHomeAddress"
        :memberLocked="memberLocked"
        :loading="memberDetailsLoading && memberAddressesLoading"
        :editable="true"
        :showNameFields="false"
        :showForm="true"
        :addresses="memberAddressesWithExtras"
        v-model="computedContactInformation"
        @ready="isReady => (ready.contact = isReady)"
      />

      <reporting-codes
        :memberLocked="memberLocked"
        :memberDetailsLoading="memberDetailsLoading"
        :subGroupPlanInfo="computedCurrentSubGroupPlanInfo"
        v-model="computedReportingCodes"
        @ready="isReady => (ready.reporting = isReady)"
      />

      <privacy-address
        :memberLocked="memberLocked"
        :memberDetailsLoading="memberDetailsLoading"
        v-model="computedPrivacyAddress"
        @ready="isReady => (ready.privacy = isReady)"
      />

      <language-assistance
        :memberLocked="memberLocked"
        :memberDetailsLoading="memberDetailsLoading"
        v-model="computedLanguageAssistance"
        class="_bb1"
      />
    </accordion>
    <section>
      <page-actions
        class="_flex justify-center align-center"
        :isSubmitting="isSubmitting"
        :savingDraft="savingDraft"
        :cancel-route="{
          name: 'member-details',
          params: { memberCk: memberDetails.member.memberCk }
        }"
        :submitEnabled="allFormsReady"
        @submit="submit"
        @saveDraft="saveDraft"
      />
    </section>

    <cloud-cannon-content
      class="_pt0 _pbxs bg-light-gray"
      :content-loading="cloudCannonContentLoading"
      :content="cloudCannonContent.footer"
    />
  </div>
</template>

<script>
/** Mixins */
  import fetchable from '@mixins/fetchable'

  /** Components */
  import Accordion from '@sections/Accordion'
  import AccordionCollapser from '@molecules/AccordionCollapser'

  /** Sections */
  import PageTitle from './MemberEdit/PageTitle'
  import PageActions from './MemberEdit/PageActions'
  import MemberCoverage from './MemberPartials/MemberCoverage'
  import PersonalInformation from './MemberEdit/PersonalInformation'
  import RelatedInformation from './MemberPartials/RelatedInformation'
  import ContactInformation from './MemberPartials/ContactInformation'
  import ReportingCodes from './MemberEdit/ReportingCodes'
  import PrivacyAddress from './MemberEdit/PrivacyAddress'
  import LanguageAssistance from './MemberEdit/LanguageAssistance'

  import memberManagement from '@mixins/memberManagement'
  import memberEdit from '@mixins/memberEdit'
  import formattable from '@mixins/formattable'
  import ClassPlanFromSubGroupCkData from '@mixins/ClassPlanFromSubGroupCkData'
  import SubmitMember from '@mixins/SubmitMember'
  import CloudCannonable from '@mixins/CloudCannonable'

  import { MEMBER_UPDATE_SUCCESSFUL } from '@api/Statuses'
  import { fileBoundIdValidation } from '@utils'
  import { alphaNum, maxLength, requiredIf } from 'vuelidate/lib/validators'

  export default {
    mixins: [
      fetchable,
      memberManagement,
      memberEdit,
      formattable,
      ClassPlanFromSubGroupCkData,
      SubmitMember,
      CloudCannonable
    ],
    components: {
      Accordion,
      AccordionCollapser,
      PageTitle,
      PageActions,
      MemberCoverage,
      PersonalInformation,
      RelatedInformation,
      ContactInformation,
      ReportingCodes,
      PrivacyAddress,
      LanguageAssistance
    },
    computed: {
      memberCk() {
        return this.$store.getters['hiddenRouteData/getHiddenRouteData'].memberCk
      },
      fetchers() {
        return [
          {
            action: 'GET_MEMBER_DETAILS',
            options: {
              memberCk: this.memberCk,
              params: {
                isEligibility: false,
                isBenefit: false,
                dateOfService: this.now()
              }
            }
          },
          {
            action: 'cloudCannon/GET_CLOUD_CANNON_CONTENT',
            options: {
              cloudCannonResource: '/member-details.json'
            },
            config: {
              root: true
            }
          }
        ]
      }
    },
    data() {
      return {
        fileBoundId: '',
        savingDraft: false,
        subGroupCk: '',
        ready: {
          personal: true,
          contact: true,
          privacy: true,
          reporting: true
        }
      }
    },
    methods: {
      saveDraft() {
        this.savingDraft = true

        this.updateMember('Hold')
          .then(res => {
            if (res.status !== MEMBER_UPDATE_SUCCESSFUL) {
              console.warn('Member update not successful', res.status)
              // TODO: handle not successful
              return
            }

            this.$store.commit('SET_ANNOUNCEMENT', 'Member draft updated')
          })
          .catch(_ => {
          // TODO: catch error
          })
          .finally(() => {
            this.isSubmitting = false
          })
      }
    },
    created() {
      if (!this.memberManagementAllowed) {
        this.$store.commit(
          'SET_ANNOUNCEMENT_WARNING'
        )
        this.$store.commit(
          'SET_ANNOUNCEMENT',
          'You do not have member management access for this member'
        )

        this.$router.push({
          name: 'member-details',
          params: {
            memberCk: this.memberCk
          }
        })
        return
      }
      // we wouldn't normally just do a map state on this but it's subgroupCk in the payload from the API
      // but our watchers for our helpers are listening to subGroupCk (captial G)
      // This whole watch/created pattern for this.subGroupCk can probably be refactored to used computed instead
      if (this.memberDetails && this.memberDetails.member.subgroupCk) {
        this.subGroupCk = this.memberDetails.member.subgroupCk
      }

      if (this.memberDetails && this.memberDetails.member.subscriberCk) {
        this.$store.dispatch('GET_MEMBER_ADDRESSES', { memberCk: this.memberDetails.member.subscriberCk })
      }
    },
    validations() {
      return {
        internalValue: {
          clientHealthId: {
            required: requiredIf(function() {
              return this.memberIdSource === 'CS'
            }),
            maxLength: maxLength(15),
            alphaNum
          },
          fileBoundId: {
            required: requiredIf(function() {
              return this.$isInternalUser
            }),
            maxLength:
              this.$isInternalUser && maxLength(15),
            fileBoundIdValidation
          }
        }
      }
    }
  }
</script>
